import {
  DataTable,
  FAIcon,
  HideInProduction,
  HoverColorPicker,
  OnlyInProduction,
} from '@/components'
import type { AbsenceTypeNode } from '@/modules/timesheets/types.graphql'
import type { ColumnDef } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useCallback, useMemo } from 'react'
import type { AbsenceTypesListProps } from './types'
import { AbsenceDialog } from './AbsenceDialog'

interface AbsenceSettingsTableProps extends AbsenceTypesListProps {
  temporaryEditColor: {
    [id: string]: string | null
  }
  onUpdateColor: (categoryId: string) => (color: string) => void
}

export const AbsenceSettingsTable: React.FC<AbsenceSettingsTableProps> = ({
  absenceTypes,
  temporaryEditColor,

  onEditSubmit,
  onDelete,
  onUpdateColor,
}) => {
  const translations = useTranslate({
    name: 'common.name',

    add: 'common.add',
    edit: 'common.edit',
    delete: 'common.delete-alt',
    cancel: 'common.cancel',
    id: 'common.id',

    numTypes: ['settings.absence.absence-types.num-types', { n: 0 }],
  })

  const handleEditClick = useCallback(
    (type: AbsenceTypeNode) => {
      onEditSubmit(type)
    },
    [onEditSubmit]
  )

  const tableColumns = useMemo<ColumnDef<AbsenceTypeNode>[]>(
    () => [
      {
        accessorKey: 'id',
        header: '',
        meta: {
          narrow: true,
        },
        cell: ({ row }) => {
          const { id, color } = row.original

          return (
            <HoverColorPicker
              value={temporaryEditColor[id] ?? color}
              onChange={onUpdateColor(id)}
            />
          )
        },
      },
      {
        accessorKey: 'externalId',
        header: translations.id,
        enableSorting: true,
        meta: {
          narrow: true,
          center: true,
        },
      },
      {
        accessorKey: 'name',
        header: translations.name,
        enableSorting: true,
      },
      {
        accessorKey: 'edit',
        header: '',
        meta: {
          narrow: true,
        },
        cell: ({ row }) => {
          const type = row.original

          return (
            <>
              <HideInProduction>
                <AbsenceDialog absenceType={type} />
              </HideInProduction>

              <OnlyInProduction>
                <FAIcon
                  className="text-gray-400 hover:text-red-500"
                  icon="edit"
                  cursor="pointer"
                  onClick={() => handleEditClick(type)}
                />
              </OnlyInProduction>
            </>
          )
        },
      },
      {
        accessorKey: 'delete',
        header: '',
        meta: {
          narrow: true,
        },
        cell: ({ row }) => {
          const type = row.original

          return (
            <FAIcon
              className="text-gray-400 hover:text-red-500"
              icon="times"
              cursor="pointer"
              onClick={() => onDelete(type)}
            />
          )
        },
      },
    ],
    [
      handleEditClick,
      onDelete,
      onUpdateColor,
      temporaryEditColor,
      translations.id,
      translations.name,
    ]
  )

  const tableData = useMemo(() => absenceTypes, [absenceTypes])

  return (
    <DataTable
      columns={tableColumns}
      data={tableData}
      pagination={{
        itemsText: n => translations.numTypes({ n }),
      }}
    />
  )
}
